import {
  castArray,
  difference,
  uniq,
  pick,
  isArray,
  get,
  flattenDeep,
  isEmpty,
  startsWith,
} from 'lodash-es'
import { subscribeWithSelector } from 'zustand/middleware'
import create from 'zustand/vanilla'
import {
  fetchCountGroupBy as fetchCountGroupByApi,
  fetchCountIndexOfElement as fetchCountIndexOfElementApi,
  fetchCountDocumentByGroupBySlugs as fetchCountDocumentByGroupBySlugsApi,
} from '@vt/apis/lib-ex/v2/tables'
import RefType from '../ref-type'

// 处理换缓存的引用字段
const ensureRefFields = function (fields) {
  return fields
    .filter((field) => {
      const isRef =
        RefType.includes(field.type) && !['app'].includes(field.refType)

      const isBindStandard = startsWith(field.slug, 'is_standard_')

      const validBind =
        !isBindStandard || (isBindStandard && field.isBindStandard === true)

      return isRef && validBind
    })
    .map((field) => ({
      ids: [],
      ...field,
    }))
}

const getRefType = function (field) {
  if (field.type === 'userMultiple') {
    return 'user'
  }
  if (field.type === 'orgMultiple') {
    return 'org'
  }
  if (['ref', 'sysref'].includes(field.type)) {
    return field.tbId || field.dataFromTbId
  }
  return field.type
}

export default class Base {
  constructor(appId, tbNameId, vtProject) {
    // const app = vtProject.getApp(appId)
    // const { tbs } = app

    this.vtProject = vtProject

    this.appId = appId
    this.scope = null
    this.data = []
    this.tbNameId = tbNameId
    // this.fields = []
    this.db = vtProject.getDittoDBByAppId(appId)

    this.total = 0 // 数据总数
    this.updatedAt = null // 上次更新时间
    this.lastUpdatedTbDataIds = [] // 记录上次更新的ids

    // tbDefs 相关
    // const tbDefs = tbs[tbNameId] || {}
    // this.tbSlug = tbDefs.tbSlug || tbNameId
    // this.fields = tbDefs.fields || []
    // this.tbDefs = tbDefs
    // this.tbId = tbDefs.tbId
    // this.tbs = tbs

    this.useStore = create(subscribeWithSelector(() => ({})))

    // 数据存储相关
    this.processedData = {
      // 只存储 user org area standard类型数据,slug要与this.data对应
      // id: {  user, }
    }
  }

  get app() {
    return this.vtProject.getApp(this.appId)
  }

  get tbs() {
    return this.app.tbs
  }

  get tbDefs() {
    return this.tbs[this.tbNameId] || {}
  }

  get tbSlug() {
    return this.tbDefs.tbSlug || this.tbNameId
  }

  get fields() {
    return this.tbDefs.fields || []
  }

  get tbId() {
    return this.tbDefs.tbId
  }

  get dbSpecId() {
    return `${this.dbSpecPrefix}_lsd`
  }

  get refetchGap() {
    return new Date().getTime() - this.lastStoreDBAt > 60 * 1000 * 60
  }

  get isNeedRefetch() {
    if (!this.lastStoreDBAt) {
      return true
    }

    // 12 分钟
    if (this.refetchGap) {
      return true
    }
    return false
  }

  get meta() {
    return {
      appId: this.appId,
      _id: this.dbSpecId,
      time: new Date().getTime(),
    }
  }

  init() {
    console.log('[use-tbview-data] init', this.dbSpecPrefix)
    return this.db
      .findById(this.dbSpecId)
      .then((data) => {
        const { time } = data
        this.lastStoreDBAt = time
        if (this.isNeedRefetch) {
          const atime = new Date().getTime()
          console.log(
            '[use-tbview-data] 缓存失效，准备获取数据',
            this.dbSpecPrefix
          )
          return this.fetchAll(this.appId).then((fetchedData) => {
            console.log(
              '[use-tbview-data] 获取数据完毕, 花费时间',
              (new Date().getTime() - atime) / 1000,
              '秒'
            )

            return this.store(fetchedData)
          })
        }
        return Promise.resolve(true)
      })
      .catch((err) => {
        console.log('[use-tbview-data] store error', this.dbSpecPrefix, err)
        return Promise.resolve(false)
      })
  }

  // eslint-disable-next-line class-methods-use-this
  async getByIds(ids) {
    if (!ids || ids.length === 0) {
      return Promise.resolve([])
    }
    const finded = await this.db.findByIds(ids.map((id) => this.makeId(id)))
    const findedIds = finded.map((x) => x._id)
    const restIds = difference(ids, findedIds)
    if (restIds.length > 0) {
      const rest = await this.fetchByIds(restIds)
      return finded.concat(rest)
    }
    return finded
  }

  async getById(id) {
    if (!id) {
      return Promise.resolve(null)
    }
    const finded = await this.db.findById(this.makeId(id))

    if (!finded) {
      return this.fetchById(id)
    }
    return finded
  }

  makeId(id) {
    return `${this.dbSpecPrefix}_${id}`
  }

  toDB(data) {
    return data.map((d) => {
      return {
        ...d,
        appId: this.appId,
        _id: this.makeId(d[this.idFiledName]),
        // _rev: d[this.idFiledName],
      }
    })
  }

  async store(data) {
    if (!data || data.length === 0) {
      return Promise.resolve(false)
    }

    const models = this.toDB(data)

    const atime = new Date().getTime()
    console.log(
      '[use-tbview-data] 获取到数据',
      this.dbSpecPrefix,
      `${models.length} 条`
    )

    await this.db.saveMeta(this.meta)

    return this.db
      .bulkSave(models)
      .then(() => {
        this.lastStoreDBAt = new Date().getTime()
        console.log(
          '[use-tbview-data] 存储完毕, 花费时间',
          (new Date().getTime() - atime) / 1000,
          '秒'
        )
        // handle response
        return true
      })
      .catch((err) => {
        console.log('[use-tbview-data] store error', this.dbSpecPrefix, err)
        return Promise.resolve(false)
      })
  }

  all() {
    if (this.scope === 'all') {
      return Promise.resolve(this.data)
    }
    return Promise.resolve({})
  }

  getDitto(tbNameId) {
    return this.vtProject.getDitto(this.appId, tbNameId)
  }

  // 把数据中要预处理的字段换成 Promise, 只是单行数据
  // getDatumMaskedWithPromise(datum) {

  // }

  async getByIdsWithDeal(ids) {
    const data = this.getByIds(ids)
    return this.dealLazyData(data)
  }

  maskData(data) {
    const refFields = ensureRefFields(this.fields)
    const mask = {}
    // eslint-disable-next-line no-return-assign
    refFields.forEach((x) => (mask[x.slug] = null))
    return data.map((datum) => {
      return { ...datum, ...mask }
    })
  }

  lazyData(data) {
    const refFields = ensureRefFields(this.fields)
    return data.map((datum) => {
      const slugs = refFields.map((field) => {
        return field.slug
      })
      return pick(datum, slugs)
    })
  }

  dealLazyData(data = []) {
    if (data.length === 0) {
      return Promise.resolve([])
    }

    const refFields = ensureRefFields(this.fields)

    data
      .filter((v) => v)
      .forEach((datum) => {
        refFields.forEach((field) => {
          if (datum[field.slug]) {
            const ids = flattenDeep(castArray(datum[field.slug]))

            ids.map((id) => field.ids.push(id))
          }
        })
      })

    const mergedFields = {}
    refFields.forEach((field) => {
      const typeName = getRefType(field)
      if (!typeName) return
      if (!mergedFields[typeName]) mergedFields[typeName] = []
      mergedFields[typeName] = mergedFields[typeName].concat(field.ids)
    })

    const mapOfDealedData = {}

    const mfp = Object.entries(mergedFields).map(([tbNameId, ids]) => {
      const ditto = this.getDitto(tbNameId)
      if (ditto) {
        return ditto
          .getByIds(uniq(ids))
          .then(async (lds) => {
            const dealedDataLds = await ditto.dealLazyData(lds)
            mapOfDealedData[tbNameId] = {}
            dealedDataLds.forEach(
              // eslint-disable-next-line no-return-assign
              (ld) => (mapOfDealedData[tbNameId][ld[ditto.idFiledName]] = ld)
            )
            return dealedDataLds
          })
          .catch((err) => {
            console.warn('[model base] 预处理数据遇到问题', err)
            return []
          })
      }
      console.error('[model base] 不能正确获取关联表的 ditto 实例', tbNameId)
      return Promise.resolve([])
    })
    console.log(mfp, 'mfp')
    // console.warn('[abc]', mergedFields)
    return Promise.all(mfp)
      .then(() => {
        const allDealedData = data
          .filter((v) => v)
          .map((datum) => {
            const dealedData = { ...datum }
            refFields.forEach((field) => {
              const value = datum[field.slug]
              if (!value) return
              const typeName = getRefType(field)
              if (!mapOfDealedData[typeName]) return
              if (isArray(value)) {
                // dealedData[field.slug] = Object.values(pick(mapOfDealedData[typeName], value))
                // 处理二维数组 比如指标
                dealedData[field.slug] = value
                  .map((val) => {
                    if (isArray(val)) {
                      return Object.values(pick(mapOfDealedData[typeName], val))
                    }
                    return get(mapOfDealedData, [typeName, val])
                  })
                  // 过滤掉空数据
                  .filter(
                    (v) => (!isArray(v) && v) || (isArray(v) && !isEmpty(v))
                  )
              } else {
                dealedData[field.slug] = mapOfDealedData[typeName][value]
              }
              // if (field.slug === 'standard') {
              //   console.warn(
              //     '[model base] 完成数据处理:',
              //     field.slug,
              //     datum[field.slug],
              //     dealedData[field.slug]
              //   )
              // }
            })
            return dealedData
          })
        console.log('[model base] 完成数据处理条数：', allDealedData.length)
        console.log(allDealedData, '预处理后的数据')
        return allDealedData
      })
      .catch((err) => {
        console.warn('[model base] 预处理数据出错，返回原始数据', err)
        return data
      })
  }

  // 获取数组类型数据统计数量
  fetchCountGroupBy(slugs, dsl) {
    return fetchCountGroupByApi(this.appId, this.tbId, slugs, dsl).then(
      (res) => {
        return res
      }
    )
  }

  // 根据层级统计数据（比如点位）
  fetchCountIndexOfElement(slugs, { filters }, level) {
    return fetchCountIndexOfElementApi(
      this.appId,
      this.tbId,
      slugs,
      {
        filters,
      },
      (level && level - 1) || 0
    ).then((res) => {
      return res
    })
  }

  // 根据某个字段分组统计数据
  fetchCountDocumentByGroupBySlugs(slugs, { filters }) {
    return fetchCountDocumentByGroupBySlugsApi(this.appId, this.tbId, slugs, {
      filters,
    }).then((res) => {
      return res
    })
  }
  // preProcessData(data) {
  //   const refFields = ensureRefFields(this.fields)
  //   const maskedData = data.map((datum) => {
  //     const slugs = refFields.map((field) => {
  //       if (datum[field.slug]) {
  //         const ids = castArray(datum[field.slug])
  //         ids.map((id) => field.ids.push(id))
  //       }
  //       return field.slug
  //     })
  //     return pick(datum, slugs)
  //   })
  //   console.warn(maskedData)
  //   const mergedFields = {}
  //   refFields.forEach((field) => {
  //     const typeName = getRefType(field)
  //     if (!mergedFields[typeName]) mergedFields[typeName] = []
  //     mergedFields[typeName] = mergedFields[typeName].concat(field.ids)
  //   })
  //   const mfp = Object.entries(mergedFields).map(([tbName, ids]) => {
  //     const ditto = this.getDitto(tbName)
  //     if (ditto) {
  //       return ditto.getByIds(uniq(ids))
  //     }
  //     return Promise.resolve([])
  //   })
  //   console.warn(mergedFields)
  //   return Promise.all(mfp)
  // }

  /**
   * 按ids以及自定义filters批量获取数据，并进行预处理数据
   * @param {*} ids
   * @returns
   */
  fetchByIds(ids, filters = {}) {
    const { queries = [] } = filters
    if (!ids) {
      return Promise.resolve([])
    }
    const arredIds = castArray(ids)

    return this.fetchTbDataRaw({
      filters: {
        logicType: 'and',
        queries: [
          {
            slug: this.idFiledName,
            queryType: ['equal'], // inList
            value: arredIds.length === 1 ? arredIds[0] : arredIds,
          },
          ...queries,
        ],
      },
      pageSize: arredIds.length,
      pageNum: 0,
      useAuth: false,
    }).then((res) => {
      // hooks 处理了，这儿多处理的一遍，导致预处理数据时接口多请求了一遍！！！
      // this.afterDataChanged(res.tbData)
      return res.tbData
    })
  }
}
